import { createRouter, createWebHistory } from 'vue-router'
import { useFormStore } from '../stores/formStore'
import AccessDenied from '../views/AccessDenied.vue'
import UserChangePassword from '../views/UserChangePassword.vue'
import Login from '../components/Login.vue'

const routes = [
  // Route pubbliche
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false },
  },

  // Route utente normale
  {
    path: '/home',
    name: 'Home',
    component: () => import('../views/HomeUser.vue'),
    meta: { permission: 'user.home.read' },
  },
  {
    path: '/form/:formId',
    name: 'FormCompilation',
    component: () => import('../views/FormCompilation.vue'),
    meta: { permission: 'user.forms.submit' },
  },

  {
    path: '/formazione-dettaglio',
    name: 'UserFormazioneDettaglio',
    component: () => import('../views/UserFormazioneDettaglio.vue'),
    meta: { permission: 'user.home.read' },
  },
  {
    path: '/corsi-frequentati',
    name: 'UserCorsiDettaglio',
    component: () => import('../views/UserCorsiDettaglio.vue'),
    meta: { permission: 'user.home.read' },
  },
  {
    path: '/storico-compilazioni',
    name: 'UserFormDettaglio',
    component: () => import('../views/UserFormDettaglio.vue'),
    meta: { permission: 'user.home.read' },
  },
  {
    path: '/storico-compilazioni/:compilationId',
    name: 'UserCompilationAnswers',
    component: () => import('../views/UserCompilationAnswers.vue'),
    meta: { permissionAll: ['user.home.read', 'user.forms.submit'] },
  },

  // Route admin per compilazione form (distinguibile dall'URL)
  {
    path: '/admin/form/:formId',
    name: 'AdminFormCompilation',
    component: () => import('../views/FormCompilation.vue'),
    meta: { permission: 'admin.forms.compile' },
  },
  {
    path: '/user/change-password',
    name: 'UserChangePassword',
    component: UserChangePassword,
    meta: { permission: 'user.password.change' },
  },
  // Route admin
  {
    path: '/admin/home',
    name: 'AdminHome',
    component: () => import('../views/admin/Home.vue'),
    meta: { permission: 'admin.home.read' },
  },
  {
    path: '/admin/user-management',
    name: 'UserManagement',
    component: () => import('../views/admin/UserManagement.vue'),
    meta: { permission: 'admin.users.read' },
  },
  {
    path: '/admin/compile-forms',
    name: 'AdminCompileForms',
    component: () => import('../views/admin/UserFormSelector.vue'),
    meta: { permission: 'admin.forms.compile' },
  },
  {
    path: '/admin/change-password',
    name: 'AdminChangePassword',
    component: UserChangePassword,
    meta: { permission: 'user.password.change' },
  },
  {
    path: '/admin/training/participants',
    name: 'TrainingParticipants',
    component: () => import('../views/admin/TrainingParticipants.vue'),
    meta: {
      permissionAny: [
        'admin.training.internal.conferenza.read',
        'admin.training.internal.workshop.read',
        'admin.training.internal.funded.read',
        'admin.training.internal.corso.external_teacher.read',
        'admin.training.internal.corso.read',
      ],
    },
  },
  {
    path: '/admin/training/create',
    name: 'TrainingTitleCreation',
    component: () => import('../views/admin/TrainingTitleCreation.vue'),
    meta: {
      permissionAny: [
        'admin.training.internal.conferenza.create',
        'admin.training.internal.workshop.create',
        'admin.training.internal.funded.create',
        'admin.training.internal.corso.external_teacher.create',
        'admin.training.internal.corso.create',
      ],
    },
  },
  {
    path: '/admin/external/proposals',
    name: 'ExternalProposals',
    component: () => import('../views/admin/ExternalProposals.vue'),
    meta: { permission: 'admin.training.external.proposal' },
  },
  {
    path: '/admin/external/form-defaults',
    name: 'ExternalFormDefaults',
    component: () => import('../views/admin/ExternalFormDefaultsManager.vue'),
    meta: { permission: 'admin.training.external.read' },
  },
  {
    path: '/admin/training/edit/:id',
    name: 'TrainingTitleEdit',
    component: () => import('../views/admin/TrainingTitleEdit.vue'),
    meta: {
      permissionAny: [
        'admin.training.internal.conferenza.write',
        'admin.training.internal.workshop.write',
        'admin.training.internal.funded.write',
        'admin.training.internal.corso.external_teacher.write',
        'admin.training.internal.corso.write',
      ],
    },
  },
  {
    path: '/admin/form3-analytics',
    name: 'Form3Analytics',
    component: () => import('../views/admin/Form3Analytics.vue'),
    meta: {
      permissionAny: [
        'admin.analytics.form3.read.1',
        'admin.analytics.form3.read.2',
        'admin.analytics.form3.read.3',
        'admin.analytics.form3.read.4',
        'admin.analytics.form3.speaker.read',
      ],
    },
  },
  {
    path: '/admin/form4-analytics',
    name: 'Form4Analytics',
    component: () => import('../views/admin/Form4Analytics.vue'),
    meta: { permission: 'admin.analytics.form4.read' },
  },
  {
    path: '/admin/form4-analytics-details',
    name: 'Form4AnalyticsDetails',
    component: () => import('../views/admin/Form4AnalyticsDetails.vue'),
    meta: { permission: 'admin.analytics.form4.read' },
  },
  {
    path: '/admin/title/:id/:cycleId/edit',
    name: 'TitleEdit',
    component: () => import('../views/admin/EventEdit.vue'),
    meta: {
      permissionAny: [
        'admin.training.internal.conferenza.write',
        'admin.training.internal.workshop.write',
        'admin.training.internal.funded.write',
        'admin.training.internal.corso.external_teacher.write',
        'admin.training.internal.corso.write',
      ],
    },
  },
  {
    path: '/admin/title/:id/:cycleId/detail',
    name: 'TitleDetail',
    component: () => import('../views/admin/EventDetail.vue'),
    meta: {
      permissionAny: [
        'admin.training.internal.conferenza.read',
        'admin.training.internal.workshop.read',
        'admin.training.internal.funded.read',
        'admin.training.internal.corso.external_teacher.read',
        'admin.training.internal.corso.read',
      ],
    },
  },
  {
    path: '/admin/controllo-form-obbligatori',
    name: 'ControlloFormObbligatori',
    component: () => import('../views/Admin/MandatoryFormsControl.vue'),
    meta: { permission: 'admin.forms.mandatory.read' },
  },
  // Route di accesso negato
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: AccessDenied,
    meta: { requiresAuth: false },
  },

  // Reindirizzamento per la root route
  {
    path: '/',
    redirect: () => {
      const store = useFormStore()
      return store.isAuthenticated ? store.getHomeRoute() : '/login'
    },
  },

  // Reindirizzamento per route non trovate
  {
    path: '/:pathMatch(.*)*',
    redirect: '/access-denied',
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Guardia di navigazione globale
router.beforeEach(async (to, from, next) => {
  // Se la route richiede autenticazione o non è esplicitamente specificato
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth !== false)

  const store = useFormStore()

  // Inizializza il router nello store se non è già impostato
  if (!store.router) {
    store.router = router
  }

  // Se non è necessaria autenticazione per questa route (es. login)
  if (!requiresAuth) {
    // Se l'utente è già autenticato e sta cercando di accedere al login, reindirizza alla home
    if (to.path === '/login' && store.isAuthenticated) {
      return next(store.getHomeRoute())
    }
    return next()
  }

  // Per route che richiedono autenticazione

  // Se l'utente non è autenticato, verifica la sessione
  if (!store.isAuthenticated) {
    try {
      await store.checkSession()
    } catch (error) {
      console.error('Errore nella verifica sessione:', error)
      // In caso di errore, reindirizza al login
      return next({ path: '/login', replace: true })
    }
    // checkSession può ritornare false senza lanciare (es. sessione scaduta silenziosamente)
    if (!store.isAuthenticated) {
      return next({ path: '/login', replace: true })
    }
  }

  // Controlla se l'utente ha i permessi richiesti (singolo o lista)
  const singlePerm = typeof to.meta.permission === 'string' ? [to.meta.permission] : []
  const requiredAny = Array.isArray(to.meta.permissionAny) ? to.meta.permissionAny : singlePerm
  if (requiredAny.length > 0 && (!store.user || !requiredAny.some(p => store.hasPermission(p)))) {
    console.warn(`Accesso negato: nessun permesso tra [${requiredAny.join(', ')}]`)
    return next({ path: '/access-denied', replace: true, query: { from: to.fullPath } })
  }

  // Controlla permissionAll: tutti i permessi devono essere presenti (AND)
  if (Array.isArray(to.meta.permissionAll) && to.meta.permissionAll.length > 0) {
    if (!store.user || !to.meta.permissionAll.every(p => store.hasPermission(p))) {
      console.warn(`Accesso negato: mancano permessi in [${to.meta.permissionAll.join(', ')}]`)
      return next({ path: '/access-denied', replace: true, query: { from: to.fullPath } })
    }
  }

  // Se tutto è a posto, procedi
  next()
})

export default router
